import { createSlice } from '@reduxjs/toolkit';

import { RootState } from 'modules/store';

import { PayoutsState, QueuedPayment } from './payout.types';
import {
  getRefunds,
  updateRefund,
  optInForPayouts,
  getPayoutDetails,
  getRefundReasons,
  getQueuedPayments,
  postInitiateRefund,
  getUsersPayoutDetails,
  getPaymentRefundDetailsById,
  connectBankForPayouts,
  initiatePayout,
  cancelPayout,
  authorizePayout,
  getPayoutDestinations,
  createPayoutAuthorizationRule,
  getPayoutAuthorizationRule,
  updatePayoutAuthorizationRule,
  fetchSecretSchema,
  validateBulkPayout,
  uploadSecrets,
  connectToBankUsingSecrets,
} from './payouts.actions';

const initialState: PayoutsState = {
  endUserId: '',
  tppUserId: '',
  customerId: '',
  connected: false,
  isOptingIn: false,
  isConnecting: false,
  queuedPayments: [],
  paymentSourceId: '',
  leanAuthorizer: false,
  isOptInRequired: false,
  isAlreadyConfiguredByAnotherUser: false,
  usersPayoutDetails: null,
  totalQueuedPaymentsCount: 0,
  isFetchingPayoutDetails: false,
  isFetchingQueuedPayments: false,
  isFetchingUsersPayoutDetails: false,

  // PAYOUT INITIATION
  isInitiatingPayout: false,
  isCancellingPayout: false,
  isAuthorizing: false,

  // BULK PAYOUT
  isUploadingFile: false,
  validatedPayments: [],
  accountId: '',

  // PAYOUT DESTINATIONS
  isFetchingDestinations: false,
  destinations: [],
  totalDestinationsCount: 0,

  // PAYOUT RULES
  payoutRules: {
    isPending: false,
    isLoading: false,
    threshold: null,
    ruleId: null,
  },

  // SECRET SCHEMA
  isFetchingSecretSchema: false,
  isUploadingSecrets: false,
  isConnectingToBank: false,
  secretSchema: null,

  // REFUNDS
  paymentRefundDetails: null,
  isFetchingPaymentRefundDetails: false,
  refunds: null,
  totalRefundsCount: 0,
  isFetchingRefunds: false,
  refundReasons: null,
  isFetchingRefundReasons: false,
  isPostingRefund: false,
  isUpdatingRefund: false,
  isEmptyBeneficiaryNameError: false,
};

const payoutsSlice = createSlice({
  name: 'payouts',
  initialState,
  reducers: {
    removeValidatedPayments: (state, action) => {
      const paymentIdsToRemove = action.payload;
      state.validatedPayments = state.validatedPayments.filter(
        payment => !paymentIdsToRemove.includes(payment.id),
      );
    },
  },
  extraReducers: builder => {
    // GET-PAYOUT-DETAILS
    builder.addCase(getPayoutDetails.pending, state => {
      state.isFetchingPayoutDetails = true;
    });
    builder.addCase(getPayoutDetails.fulfilled, (state, { payload }) => ({
      ...state,
      isOptInRequired: false,
      isFetchingPayoutDetails: false,
      connected: payload.connected,
      endUserId: payload.end_user_id,
      tppUserId: payload.tpp_user_id,
      customerId: payload.payouts_customer_id,
      leanAuthorizer: payload.lean_authorizer,
      paymentSourceId: payload.payment_source_id,
    }));
    builder.addCase(getPayoutDetails.rejected, (state, { payload }) => {
      state.isFetchingPayoutDetails = false;

      if (payload && 'isOptInRequired' in payload) {
        state.isOptInRequired = payload.isOptInRequired ?? false;
      }
      if (payload && 'isAlreadyConfiguredByAnotherUser' in payload) {
        state.isAlreadyConfiguredByAnotherUser =
          payload.isAlreadyConfiguredByAnotherUser ?? false;
      }
    });

    // GET-USERS-PAYOUT-DETAILS
    builder.addCase(getUsersPayoutDetails.pending, state => {
      state.isFetchingUsersPayoutDetails = true;
    });
    builder.addCase(getUsersPayoutDetails.fulfilled, (state, { payload }) => {
      state.isFetchingUsersPayoutDetails = false;
      state.usersPayoutDetails = payload;
    });
    builder.addCase(getUsersPayoutDetails.rejected, (state, { payload }) => {
      state.isFetchingUsersPayoutDetails = false;
    });

    // OPT-IN-FOR-PAYOUT
    builder.addCase(optInForPayouts.pending, state => {
      state.isOptingIn = true;
    });
    builder.addCase(optInForPayouts.fulfilled, (state, { payload }) => ({
      ...state,
      isOptingIn: false,
      isOptInRequired: false,
      connected: payload.connected,
      endUserId: payload.end_user_id,
      tppUserId: payload.tpp_user_id,
      customerId: payload.payouts_customer_id,
      leanAuthorizer: payload.lean_authorizer,
      paymentSourceId: payload.payment_source_id,
    }));
    builder.addCase(optInForPayouts.rejected, state => {
      state.isOptingIn = false;
    });

    // CONNECT-TO-BANK-FOR-PAYOUT
    builder.addCase(connectBankForPayouts.pending, state => {
      state.isConnecting = true;
    });
    builder.addCase(connectBankForPayouts.fulfilled, (state, { payload }) => ({
      ...state,
      isConnecting: false,
      connected: payload.payout_details.connected,
      endUserId: payload.payout_details.end_user_id,
      tppUserId: payload.payout_details.tpp_user_id,
      customerId: payload.payout_details.payouts_customer_id,
      leanAuthorizer: payload.payout_details.lean_authorizer,
      paymentSourceId: payload.payout_details.payment_source_id,
    }));
    builder.addCase(connectBankForPayouts.rejected, state => {
      state.isConnecting = false;
    });

    // FETCH-QUEUED-PAYMENTS
    builder.addCase(getQueuedPayments.pending, state => {
      state.isFetchingQueuedPayments = true;
      state.totalQueuedPaymentsCount = 0;
      state.queuedPayments = [];
    });
    builder.addCase(getQueuedPayments.fulfilled, (state, { payload }) => {
      const queuedPayments = payload.content || [];
      const totalQueuedPaymentsCount = payload.total_elements || 0;

      return {
        ...state,
        totalQueuedPaymentsCount,
        isFetchingQueuedPayments: false,
        queuedPayments: queuedPayments.map((qp: QueuedPayment) => ({
          ...qp,
          id: qp.payment_intent_id,
          payment_intent_id: qp.payment_intent_id,
          amount: qp.amount,
          isSelectable: qp.pending_manual_authorization,
        })),
      };
    });
    builder.addCase(getQueuedPayments.rejected, state => {
      state.isFetchingQueuedPayments = false;
    });

    // FETCH PAYOUTS DESTINATIONS
    builder.addCase(getPayoutDestinations.pending, state => {
      state.isFetchingDestinations = true;
    });
    builder.addCase(getPayoutDestinations.fulfilled, (state, { payload }) => {
      state.isFetchingDestinations = false;
      state.destinations = payload.content;
      state.totalDestinationsCount = payload.page.total_elements;
    });
    builder.addCase(getPayoutDestinations.rejected, state => {
      state.isFetchingDestinations = false;
    });

    // INITIATE PAYOUT
    builder.addCase(initiatePayout.pending, state => {
      state.isInitiatingPayout = true;
    });
    builder.addCase(initiatePayout.fulfilled, (state, { payload }) => {
      state.isInitiatingPayout = false;
    });
    builder.addCase(initiatePayout.rejected, state => {
      state.isInitiatingPayout = false;
    });

    // CANCEL PAYOUT
    builder.addCase(cancelPayout.pending, state => {
      state.isCancellingPayout = true;
    });
    builder.addCase(cancelPayout.fulfilled, state => {
      state.isCancellingPayout = false;
    });
    builder.addCase(cancelPayout.rejected, state => {
      state.isCancellingPayout = false;
    });

    // AUTHORIZE PAYOUT
    builder.addCase(authorizePayout.pending, state => {
      state.isAuthorizing = true;
    });
    builder.addCase(authorizePayout.fulfilled, state => {
      state.isAuthorizing = false;
    });
    builder.addCase(authorizePayout.rejected, state => {
      state.isAuthorizing = false;
    });

    // Validate Bulk Payout

    builder.addCase(validateBulkPayout.pending, state => {
      state.isUploadingFile = true;
    });
    builder.addCase(validateBulkPayout.fulfilled, (state, { payload }) => {
      state.isUploadingFile = false;
      state.validatedPayments = payload.payment_details.map(
        (payment, index) => ({
          ...payment,
          id: `${index}`,
        }),
      );
      state.accountId = payload.account_id;
    });
    builder.addCase(validateBulkPayout.rejected, state => {
      state.isUploadingFile = false;
    });

    // CREATE PAYOUT AUTHORIZATION RULE
    builder.addCase(createPayoutAuthorizationRule.pending, state => {
      state.payoutRules.isPending = true;
    });
    builder.addCase(
      createPayoutAuthorizationRule.fulfilled,
      (state, { payload }) => {
        state.payoutRules.isPending = false;
        state.payoutRules.threshold = payload.rules[0].data.threshold;
        state.payoutRules.ruleId = payload.rules[0].id ?? null;
      },
    );
    builder.addCase(createPayoutAuthorizationRule.rejected, state => {
      state.payoutRules.isPending = false;
    });

    // UPDATE PAYOUT AUTHORIZATION RULE
    builder.addCase(updatePayoutAuthorizationRule.pending, state => {
      state.payoutRules.isPending = true;
    });
    builder.addCase(
      updatePayoutAuthorizationRule.fulfilled,
      (state, { payload }) => {
        state.payoutRules.isPending = false;
        state.payoutRules.threshold = payload.rules[0].data.threshold;
        state.payoutRules.ruleId = payload.rules[0].id ?? null;
      },
    );
    builder.addCase(updatePayoutAuthorizationRule.rejected, state => {
      state.payoutRules.isPending = false;
    });

    // GET PAYOUT AUTHORIZATION RULE
    builder.addCase(getPayoutAuthorizationRule.pending, state => {
      state.payoutRules.isLoading = true;
    });
    builder.addCase(
      getPayoutAuthorizationRule.fulfilled,
      (state, { payload }) => {
        state.payoutRules.isLoading = false;
        state.payoutRules.threshold = payload.rules[0].data.threshold;
        state.payoutRules.ruleId = payload.rules[0].id ?? null;
      },
    );
    builder.addCase(getPayoutAuthorizationRule.rejected, state => {
      state.payoutRules.isLoading = false;
    });

    // FETCH Refunds
    builder.addCase(getRefunds.pending, state => {
      state.isFetchingRefunds = true;
    });
    builder.addCase(getRefunds.fulfilled, (state, { payload }) => {
      state.isFetchingRefunds = false;
      state.refunds = payload.content || [];
      state.totalRefundsCount = payload.page.total_elements || 0;
    });
    builder.addCase(getRefunds.rejected, state => {
      state.isFetchingRefunds = false;
    });

    // FETCH Refund Reasons
    builder.addCase(getRefundReasons.pending, state => {
      state.isFetchingRefundReasons = true;
    });
    builder.addCase(getRefundReasons.fulfilled, (state, { payload }) => {
      state.isFetchingRefundReasons = false;
      state.refundReasons = payload;
    });
    builder.addCase(getRefundReasons.rejected, state => {
      state.isFetchingRefundReasons = false;
    });

    // Get payment refund details
    builder.addCase(getPaymentRefundDetailsById.pending, state => {
      state.isFetchingPaymentRefundDetails = true;
    });
    builder.addCase(
      getPaymentRefundDetailsById.fulfilled,
      (state, { payload }) => {
        state.paymentRefundDetails = payload;
        state.isFetchingPaymentRefundDetails = false;
      },
    );
    builder.addCase(
      getPaymentRefundDetailsById.rejected,
      (state, { payload }) => {
        state.isFetchingPaymentRefundDetails = false;

        if (payload && 'isEmptyBeneficiaryNameError' in payload) {
          state.isEmptyBeneficiaryNameError =
            payload.isEmptyBeneficiaryNameError;
        }
      },
    );

    // Post Refund
    builder.addCase(postInitiateRefund.pending, state => {
      state.isPostingRefund = true;
    });
    builder.addCase(postInitiateRefund.fulfilled, state => {
      state.isPostingRefund = false;
    });
    builder.addCase(postInitiateRefund.rejected, state => {
      state.isPostingRefund = false;
    });

    // Post Refund
    builder.addCase(updateRefund.pending, state => {
      state.isUpdatingRefund = true;
    });
    builder.addCase(updateRefund.fulfilled, state => {
      state.isUpdatingRefund = false;
    });
    builder.addCase(updateRefund.rejected, state => {
      state.isUpdatingRefund = false;
    });

    // FETCH SECRET SCHEMA
    builder.addCase(fetchSecretSchema.pending, state => {
      state.isFetchingSecretSchema = true;
    });
    builder.addCase(fetchSecretSchema.fulfilled, (state, { payload }) => {
      state.isFetchingSecretSchema = false;
      state.secretSchema = payload;
    });
    builder.addCase(fetchSecretSchema.rejected, state => {
      state.isFetchingSecretSchema = false;
    });

    // UPLOAD SECRETS
    builder.addCase(uploadSecrets.pending, state => {
      state.isUploadingSecrets = true;
    });
    builder.addCase(uploadSecrets.fulfilled, state => {
      state.isUploadingSecrets = false;
    });
    builder.addCase(uploadSecrets.rejected, state => {
      state.isUploadingSecrets = false;
    });

    // CONNECT
    builder.addCase(connectToBankUsingSecrets.pending, state => {
      state.isConnectingToBank = true;
    });
    builder.addCase(connectToBankUsingSecrets.fulfilled, state => {
      state.isConnectingToBank = false;
    });
    builder.addCase(connectToBankUsingSecrets.rejected, state => {
      state.isConnectingToBank = false;
    });
  },
});

export const { removeValidatedPayments } = payoutsSlice.actions;
export default payoutsSlice.reducer;
export const selectPayoutDetails = (state: RootState) => state.root.payouts;
